import React from "react";
import Language from "../lib/Language";
import API from "../lib/TimeEditAPI";
import _ from "underscore";
import TemplateGroupList from "./TemplateGroupList";
import { TTypeNameClass } from "../types/api/APIClassTypes";
import { IGetTemplateGroupsResponse } from "../types/api/APIResponseTypes";

const toggleInArray = (array: number[], value: number) => {
    if (array.indexOf(value) === -1) {
        return [...array, value];
    }
    return _.filter(array, (val) => val !== value);
};

const addToArray = (array: number[], value: number) => {
    if (array.indexOf(value) === -1) {
        return [...array, value];
    }
    return array;
};

const removeFromArray = (array, value) => _.filter(array, (val) => val !== value);

/**
 * A saved summary filter, as SummaryWindow keeps and hands them back. The editor
 * copies its fields into state on mount and answers with them through onDone.
 */
type TSummaryFilter = {
    id: number;
    name: string;
    types: number[];
    includeMembers: number[];
    frameTime: boolean;
    templateGroup: number;
};

type TSummaryEditorProps = {
    /** The filter being edited, or nothing when a new one is being created. */
    selectedFilter?: TSummaryFilter | null;
    onDone: (
        name: string,
        id: number,
        selectedTypes: number[],
        includeMembers: number[],
        isFrameTime: boolean,
        templateGroup: number
    ) => void;
    onDelete: (filterId: number) => void;
    onCancel: () => void;
    /** Which template kind's groups to offer. */
    templateKindNumber: number;
};

type TSummaryEditorState = {
    /** The alias types to pick from, loaded on mount. */
    types: TTypeNameClass[];
    templateGroups: IGetTemplateGroupsResponse["parameters"][0];
    id: number;
    name: string;
    /** The ids of the picked types, and of those whose members are included. */
    selectedTypes: number[];
    includeMembers: number[];
    isFrameTime: boolean;
    templateGroup: number;
};

class SummaryEditor extends React.Component<TSummaryEditorProps, TSummaryEditorState> {
    constructor(props) {
        super(props);
        this.state = {
            types: [],
            id: props.selectedFilter ? props.selectedFilter.id : 0,
            name: props.selectedFilter ? props.selectedFilter.name : "",
            selectedTypes: props.selectedFilter ? props.selectedFilter.types : [],
            includeMembers: props.selectedFilter ? props.selectedFilter.includeMembers : [],
            isFrameTime: props.selectedFilter ? props.selectedFilter.frameTime : false,
            templateGroup: props.selectedFilter ? props.selectedFilter.templateGroup : 0,
            templateGroups: [],
        };
        API.findAliasTypes((types) => {
            API.getTemplateGroups(this.props.templateKindNumber, (result) => {
                this.setState({ types, templateGroups: result.parameters[0] });
            });
        });
    }

    onTypeChecked(event) {
        const typeId = parseInt(event.target.value, 10);
        this.setState({
            selectedTypes: toggleInArray(this.state.selectedTypes, typeId),
            includeMembers: addToArray(this.state.includeMembers, typeId),
        });
    }

    onTypeRemoved(typeId) {
        this.setState({
            selectedTypes: toggleInArray(this.state.selectedTypes, typeId),
            includeMembers: removeFromArray(this.state.includeMembers, typeId),
        });
    }

    onMemberChecked(typeId) {
        this.setState({ includeMembers: toggleInArray(this.state.includeMembers, typeId) });
    }

    onTemplateGroupChanged(templateGroup) {
        this.setState({ templateGroup });
    }

    onIsFrameTimeChecked(event) {
        const isChecked = event.target.value === "true" ? false : true;
        this.setState({ isFrameTime: isChecked });
    }

    onDone() {
        this.props.onDone(
            this.state.name,
            this.state.id,
            this.state.selectedTypes,
            this.state.includeMembers,
            this.state.isFrameTime,
            this.state.templateGroup
        );
    }

    onDelete() {
        this.props.onDelete(this.state.id);
    }

    onCancel() {
        this.props.onCancel();
    }

    onNameChange(event) {
        this.setState({ name: event.target.value });
    }

    isDuplicateName(types, type) {
        let isDuplicate = false;
        types.forEach((tp) => {
            if (tp.id === type.id) {
                return;
            }
            if (tp.name === type.name) {
                isDuplicate = true;
            }
        });
        return isDuplicate;
    }

    render() {
        return (
            <div>
                <div className="summaryButtonContainer">
                    <button className="uiText teButton hollow" onClick={this.onCancel.bind(this)}>
                        {Language.get("dialog_cancel")}
                    </button>
                    <button
                        className="uiText teButton"
                        disabled={this.state.name === ""}
                        onClick={this.onDone.bind(this)}
                    >
                        {Language.get("nc_dialog_done")}
                    </button>
                </div>
                <div>
                    <input
                        className="nameField"
                        type="text"
                        value={this.state.name}
                        onChange={this.onNameChange.bind(this)}
                    />
                </div>
                <div key="frameTimeCheckbox">
                    {Language.get("nc_displays_frame_time")}{" "}
                    <input
                        value={String(this.state.isFrameTime)}
                        type="checkbox"
                        checked={this.state.isFrameTime === true}
                        onChange={this.onIsFrameTimeChecked.bind(this)}
                    />
                </div>
                <div key="frameTimeTemplateGroup">
                    {Language.get("dynamic_reserv_list_reservation_situation")}
                    <br />
                    <TemplateGroupList
                        value={this.state.templateGroup || 0}
                        onObjectSelect={this.onTemplateGroupChanged.bind(this)}
                        templateGroups={this.state.templateGroups}
                    />
                </div>
                <div className="filterTypesPane">
                    {this.state.selectedTypes.map((typeId) => {
                        const type = _.find(this.state.types, (tp) => tp.id === typeId);
                        if (!type) {
                            return null;
                        }
                        return (
                            <div key={type.id}>
                                {type.name}
                                <input
                                    type="button"
                                    className="removeButton rightAligned"
                                    onClick={this.onTypeRemoved.bind(this, type.id)}
                                />
                                {_.contains(this.state.selectedTypes, type.id) ? (
                                    <div style={{ paddingLeft: "20px" }}>
                                        <input
                                            value={type.id}
                                            type="checkbox"
                                            checked={_.contains(this.state.includeMembers, type.id)}
                                            onChange={this.onMemberChecked.bind(this, type.id)}
                                        />
                                        {Language.get("nc_include_members")}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                    <div>
                        <select value={0} onChange={this.onTypeChecked.bind(this)}>
                            <option value={0}>{Language.get("nc_add_summary_type")}</option>
                            {_.filter(
                                this.state.types,
                                (type) => !_.contains(this.state.selectedTypes, type.id)
                            ).map((type) => (
                                <option key={type.id} value={type.id}>
                                    {type.name}{" "}
                                    {this.isDuplicateName(this.state.types, type)
                                        ? `(${type.extid})`
                                        : null}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                {this.state.id > 0 ? (
                    <div
                        style={{
                            display: "flex",
                            alignItems: "end",
                            justifyContent: "space-between",
                        }}
                    >
                        <input
                            type="button"
                            className="uiText teButton dangerous"
                            style={{ marginLeft: "auto" }}
                            onClick={this.onDelete.bind(this)}
                            value={Language.get("nc_list_option_delete_selected")}
                        />
                    </div>
                ) : null}
            </div>
        );
    }
}

export default SummaryEditor;
